<template>
  <div class="selector-group">
    <label class="selector-label">{{ label }}</label>
    <div class="custom-select-wrapper" v-click-outside="closeDropdown">
      <div
        class="custom-select-trigger"
        :class="{ 'is-disabled': disabled }"
        @click="onTriggerClick"
      >
        <span class="placeholder">{{ placeholder }}</span>
        <span class="arrow" :class="{ 'is-open': isOpen }">&#10094;</span>
      </div>
      <div v-if="isOpen" class="custom-select-dropdown">
        <div class="search-input-wrapper">
          <input
            ref="searchInput"
            type="text"
            class="search-input"
            :placeholder="searchPlaceholder"
            v-model="searchQuery"
            @click.stop
          />
        </div>
        <div class="custom-select-options">
          <div
            v-for="option in filteredOptions"
            :key="option.id"
            class="custom-select-option"
            :class="{ 'is-selected': isSelected(option.id) }"
            @click="onOptionClick(option)"
          >
            {{ option.name }}
          </div>
          <div
            v-if="filteredOptions.length === 0 && searchQuery.length > 0"
            class="custom-select-option is-empty"
          >
            No se encontraron resultados
          </div>
          <div
            v-if="filteredOptions.length === 0 && searchQuery.length === 0"
            class="custom-select-option is-empty"
          >
            {{ emptyMessage }}
          </div>
        </div>
      </div>
    </div>
    <!-- Pills rendered below this selector -->
    <div class="selector-pills" v-if="selectedItems.length > 0">
      <span v-for="pill in selectedItems" :key="'pill-' + pill.id" class="pill" :class="pillClass">
        {{ pill.name }}
        <button class="pill-remove" @click="onRemovePill(pill)">&times;</button>
      </span>
    </div>
  </div>
</template>

<script>
/**
 * SelectorGroup Component
 *
 * A reusable multi-select dropdown with search filtering and pills.
 * Displays a custom dropdown trigger, a search input, a filterable list
 * of options, and removable pills for each selected item.
 *
 * @prop {String}  label             - The label displayed above the dropdown.
 * @prop {String}  placeholder       - Placeholder text shown inside the trigger.
 * @prop {String}  searchPlaceholder - Placeholder text shown inside the search input.
 * @prop {String}  emptyMessage      - Message shown when there are no options.
 * @prop {Array}   options           - Available options: [{ id: String, name: String, ... }].
 * @prop {Array}   selectedItems     - Currently selected items: [{ id: String, name: String, ... }].
 * @prop {Boolean} disabled          - Whether the dropdown is disabled (grayed out).
 * @prop {String}  pillClass         - CSS class applied to each pill (e.g. 'pill--category').
 *
 * @emits toggle  - Fired when the user clicks an option. Payload: the option object.
 * @emits remove  - Fired when the user removes a pill. Payload: the pill object.
 */
export default {
  name: "SelectorGroup",

  directives: {
    /**
     * Custom directive to detect clicks outside an element.
     * Used to close the dropdown when clicking elsewhere.
     */
    "click-outside": {
      bind: function(el, binding) {
        el._clickOutsideHandler = function(event) {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event)
          }
        }
        document.addEventListener("click", el._clickOutsideHandler)
      },
      unbind: function(el) {
        document.removeEventListener("click", el._clickOutsideHandler)
        el._clickOutsideHandler = null
      }
    }
  },

  props: {
    label: {
      type: String,
      default: ""
    },
    placeholder: {
      type: String,
      default: "Seleccionar..."
    },
    searchPlaceholder: {
      type: String,
      default: "Buscar..."
    },
    emptyMessage: {
      type: String,
      default: "Sin opciones disponibles"
    },
    options: {
      type: Array,
      default: function() {
        return []
      }
    },
    selectedItems: {
      type: Array,
      default: function() {
        return []
      }
    },
    disabled: {
      type: Boolean,
      default: false
    },
    pillClass: {
      type: String,
      default: ""
    }
  },

  data: function() {
    return {
      /** Whether the dropdown menu is currently visible */
      isOpen: false,

      /** The current search query typed by the user */
      searchQuery: ""
    }
  },

  computed: {
    /**
     * Filters the available options based on the search query.
     * Performs a case-insensitive and accent-insensitive match.
     * @returns {Array} Filtered list of option objects
     */
    filteredOptions: function() {
      var query = this.normalizeText(this.searchQuery.trim())

      if (query.length === 0) {
        return this.options
      }

      var self = this
      return this.options.filter(function(option) {
        var name = self.normalizeText(option.name)
        return name.indexOf(query) !== -1
      })
    }
  },

  methods: {
    /**
     * Normalizes text for searching by converting to lowercase
     * and removing diacritical marks (accents).
     * @param {String} text - The text to normalize.
     * @returns {String} Normalized text
     */
    normalizeText: function(text) {
      return text
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
    },

    /**
     * Handles click on the dropdown trigger.
     * Does nothing if the selector is disabled.
     * Focuses the search input when opening.
     */
    onTriggerClick: function() {
      if (this.disabled) return
      this.isOpen = !this.isOpen

      if (this.isOpen) {
        var self = this
        this.$nextTick(function() {
          if (self.$refs.searchInput) {
            self.$refs.searchInput.focus()
          }
        })
      } else {
        this.searchQuery = ""
      }
    },

    /**
     * Closes the dropdown menu and clears the search query.
     */
    closeDropdown: function() {
      this.isOpen = false
      this.searchQuery = ""
    },

    /**
     * Checks whether an option is currently selected.
     * @param {String} id - The option ID to check.
     * @returns {Boolean}
     */
    isSelected: function(id) {
      return this.selectedItems.some(function(item) {
        return item.id === id
      })
    },

    /**
     * Handles click on a dropdown option.
     * Emits a 'toggle' event so the parent can add or remove the item.
     * @param {Object} option - The clicked option object.
     */
    onOptionClick: function(option) {
      this.$emit("toggle", option)
    },

    /**
     * Handles click on a pill's remove button.
     * Emits a 'remove' event so the parent can handle cascading removal.
     * @param {Object} pill - The pill object to remove.
     */
    onRemovePill: function(pill) {
      this.$emit("remove", pill)
    }
  }
}
</script>

<style scoped>
.selector-group {
  -webkit-box-flex: 1;
  -ms-flex: 1;
  flex: 1;
  min-width: 0;
}

.selector-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #666;
  margin-bottom: 6px;
}

.custom-select-wrapper {
  position: relative;
}

.custom-select-trigger {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-pack: justify;
  -ms-flex-pack: justify;
  justify-content: space-between;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  padding: 12px;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  font-size: 14px;
}

.custom-select-trigger.is-disabled {
  background: #f0f0f0;
  color: #aaa;
  cursor: not-allowed;
  opacity: 0.6;
}

.custom-select-trigger .placeholder {
  color: #999;
}

.custom-select-trigger .arrow {
  display: inline-block;
  font-size: 12px;
  color: #888;
  -webkit-transform: rotate(-90deg);
  transform: rotate(-90deg);
  -webkit-transition: -webkit-transform 0.3s ease;
  transition: transform 0.3s ease;
}

.custom-select-trigger .arrow.is-open {
  -webkit-transform: rotate(90deg);
  transform: rotate(90deg);
}

.custom-select-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #d0d0d0;
  border-top: none;
  border-radius: 0 0 4px 4px;
  z-index: 100;
  -webkit-box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.search-input-wrapper {
  padding: 8px;
  border-bottom: 1px solid #eee;
}

.search-input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  font-size: 13px;
  outline: none;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
}

.search-input:focus {
  border-color: #3366e6;
  -webkit-box-shadow: 0 0 0 2px rgba(51, 102, 230, 0.15);
  box-shadow: 0 0 0 2px rgba(51, 102, 230, 0.15);
}

.custom-select-options {
  max-height: 200px;
  overflow-y: auto;
}

.custom-select-option {
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  -webkit-transition: background-color 0.15s ease;
  transition: background-color 0.15s ease;
}

.custom-select-option:hover {
  background-color: #f0f4ff;
}

.custom-select-option.is-selected {
  background-color: #e8effd;
  font-weight: 500;
}

.custom-select-option.is-empty {
  color: #999;
  font-style: italic;
  cursor: default;
}

.selector-pills {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -ms-flex-wrap: wrap;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.pill {
  display: -webkit-inline-box;
  display: -ms-inline-flexbox;
  display: inline-flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}

.pill--category,
.pill--group,
.pill--type {
  background-color: #e0e0e0;
  color: #333;
}

.pill-remove {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  margin-left: 6px;
  padding: 0 2px;
  color: inherit;
  opacity: 0.7;
}

.pill-remove:hover {
  opacity: 1;
}
</style>
